import { useMemo } from 'react'
import useApiService from 'services/api'
import { getTargetType } from './targetTypeMap'
import {
  adaptUnifiedItemForVertical,
  adaptUnifiedListParams,
  VerticalRowConfig,
} from './unifiedResponseAdapter'

/**
 * Unified data-source actions for an opportunities target table (FIR-6995,
 * default-on since the FIR-6997 cutover).
 *
 * Returns `{ listAction, summaryAction }` bound to a vertical. The actions hit
 * the unified `/opportunities/` endpoint (resolving the vertical through
 * `getTargetType`, shaping outgoing params via `adaptUnifiedListParams`, and
 * re-nesting each row via `adaptUnifiedItemForVertical` so the per-vertical
 * columns/links/actions resolve).
 *
 * `DECommonTable` calls these as `(params)` (no target_type, no signal) — the
 * target_type is bound here and the adapter handles param/row shaping, so the
 * generic table needs no knowledge of the unified contract.
 *
 * @param vertical app vertical / sub-category slug (routed through `getTargetType`)
 * @param rowConfig how to re-nest the flat unified row for this vertical
 */
export function useUnifiedTableActions<T extends { id: number }>(
  vertical: string,
  rowConfig: VerticalRowConfig
): {
  listAction: (
    params: Record<string, any>,
    signal?: AbortSignal
  ) => Promise<{ results: T[] }>
  summaryAction: (
    params: Record<string, any>,
    signal?: AbortSignal
  ) => Promise<{ count: number; alt_count: number }>
} {
  const api = useApiService()

  // Stable scalar key for the array-valued `companyIdAliases` dep below.
  const companyIdAliasesKey = rowConfig.companyIdAliases?.join(',') ?? ''

  return useMemo(() => {
    const targetType = getTargetType(vertical)

    const listAction = async (
      params: Record<string, any>,
      signal?: AbortSignal
    ) => {
      const res = await api.getOpportunitiesList(
        targetType,
        adaptUnifiedListParams(params, targetType),
        signal
      )
      return {
        ...res,
        results: res.results.map((item) =>
          adaptUnifiedItemForVertical(
            item as Record<string, unknown>,
            rowConfig
          )
        ),
      } as unknown as { results: T[] }
    }

    const summaryAction = async (
      params: Record<string, any>,
      signal?: AbortSignal
    ) => {
      const res = await api.getOpportunitiesSummary(
        targetType,
        adaptUnifiedListParams(params, targetType),
        signal
      )
      // Unified summary returns `{ count }` (+ optional `alt_count`). Most
      // verticals only surface `count`; default `alt_count` so the shared
      // pagination/stat plumbing never reads `undefined`.
      return { count: res.count, alt_count: res.alt_count ?? 0 }
    }

    return { listAction, summaryAction }
    // Keyed on the vertical + row config; `rowConfig` object identity is not
    // stable at call sites, so depend on its scalar fields instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    vertical,
    api,
    rowConfig.nestKey,
    rowConfig.detailLinkIdField,
    companyIdAliasesKey,
  ])
}
